/** Feature-scoped adapters; imported only by the surfaces that use them. */
import * as React from "react";
import { useApiClient } from "@authe/ui-core/api";
import { createApiAnalyticsSource } from "../analyticsSource";
import { createApiExplorerSource } from "../explorerSource";
import {
  createApiProtocolDefinitionSource,
  type ProtocolDefinitionSource,
} from "@authe/fe-commons/protocol-editor";

/**
 * The `metadata-registry-api` saved-dashboards port (Datadog-style analytics
 * documents CRUD, resolved across the tenancy ladder). Speaks canonical
 * `/metadata-dashboards*` paths (mapped to `internal/metadata-registry-api` by
 * `RESOURCE_REWRITES`) and parses each blob through the shared
 * `@authe/fe-commons/metadata-analytics` schema — see `./analyticsSource`.
 * Per-panel data fetching is owned by `@authe/analytics-widgets`, not this port.
 */
export function useAnalyticsSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiAnalyticsSource(client), [client]);
}

/**
 * The transaction-explorer WRITE port — promote a browsed field to a facet,
 * assemble facets into a view, and materialize authored views into physical
 * `mv_*` relations. The explorer READS via `@authe/analytics-widgets` hooks
 * over the same client; this port only carries the mutations (see
 * `./explorerSource`).
 */
export function useExplorerSource() {
  const client = useApiClient();
  return React.useMemo(() => createApiExplorerSource(client), [client]);
}

/**
 * The protocol-definition port — the decoding catalogue, one definition with
 * its origins, the editor's schema, draft previews and per-rung writes. The
 * shared fe-commons port (the admin portal builds the same one), on canonical
 * `/metadata-protocols` paths that `RESOURCE_REWRITES` maps to the service.
 */
export function useProtocolsSource(): ProtocolDefinitionSource {
  const client = useApiClient();
  return React.useMemo(() => createApiProtocolDefinitionSource(client), [client]);
}
